import { FiLink2 } from 'react-icons/fi';

export const coherenceCohesionData = {
  courseId: 'coherence-cohesion',
  courseType: 'writing-course',
  title: 'Coherence & Cohesion',
  icon: FiLink2,
  overview: {
    impact: 'Worth 25% of your Writing score',
    focus: 'Linking ideas smoothly',
    uses: 'Task 1 and Task 2',
    duration: '25 minutes',
  },
  sections: [
    {
      title: 'What Are Coherence and Cohesion?',
      content: `<p>These are two related but different concepts:</p>`,
      table: {
        headers: ['Concept', 'Definition', 'How to Achieve It'],
        rows: [
          [
            'Coherence',
            'Logical flow of ideas',
            'Clear structure, progression of arguments',
          ],
          [
            'Cohesion',
            'Words that link ideas',
            'Linking words, pronouns, reference words',
          ],
        ],
      },
    },
    {
      title: 'Linking Words by Function',
      content: `<p>Use these to connect ideas smoothly:</p>`,
      table: {
        headers: ['Function', 'Linking Words'],
        rows: [
          [
            'Addition',
            'Furthermore, Moreover, In addition, Additionally, Also',
          ],
          [
            'Contrast',
            'However, Nevertheless, On the other hand, Despite this, Conversely',
          ],
          ['Cause/Effect', 'Therefore, Consequently, As a result, Thus, Hence'],
          [
            'Example',
            'For instance, For example, Such as, Namely, To illustrate',
          ],
          ['Sequence', 'Firstly, Secondly, Finally, Subsequently, Next'],
          ['Conclusion', 'In conclusion, To summarize, Overall, In summary'],
        ],
      },
    },
    {
      title: 'Reference Words',
      content: `<p>Use these to avoid repetition and create cohesion:</p>`,
      points: [
        'Pronouns: it, they, this, these, such',
        'Determiners: this, that, these, those',
        'Synonyms and paraphrasing',
        'Summary words: this issue, these factors, the phenomenon',
      ],
      examples: [
        {
          label: 'Repetitive',
          text: 'Technology helps students. Technology makes learning easier. Technology is important.',
        },
        {
          label: 'Cohesive',
          text: 'Technology helps students by making learning easier. This development is particularly important in modern education.',
        },
      ],
    },
    {
      title: 'Common Cohesion Mistakes',
      content: `<p>Avoid these errors:</p>`,
      points: [
        '<strong>Overusing linking words:</strong> Don\'t start every sentence with "Moreover"',
        '<strong>Wrong linking word:</strong> Using "however" when ideas agree',
        '<strong>Unclear reference:</strong> "This" without clear meaning',
        "<strong>Forced transitions:</strong> Using big words that don't fit",
      ],
      warning:
        'Quality over quantity! One well-placed linking word is better than forcing them everywhere.',
    },
  ],
  exercises: [
    {
      title: 'Choose the Right Linker',
      type: 'select',
      instructions: 'Select the best linking word for each gap.',
      questions: [
        {
          text: 'Technology improves access to education. ___, it can also create digital divides.',
          options: ['However', 'Therefore', 'Moreover'],
          answer: 'However',
          explanation:
            '"However" signals contrast between a positive and negative aspect.',
        },
        {
          text: 'Many countries have invested in renewable energy. ___, solar power capacity has doubled.',
          options: ['As a result', 'However', 'For example'],
          answer: 'As a result',
          explanation: '"As a result" shows cause and effect relationship.',
        },
        {
          text: 'There are several benefits to remote work. ___, employees save commuting time.',
          options: ['For instance', 'However', 'In conclusion'],
          answer: 'For instance',
          explanation:
            '"For instance" introduces a specific example of a benefit.',
        },
        {
          text: 'The cost of living has increased significantly. ___, housing prices have risen by 40%.',
          options: ['In particular', 'However', 'Therefore'],
          answer: 'In particular',
          explanation:
            '"In particular" emphasizes a specific example within a general statement.',
        },
        {
          text: 'Online learning offers flexibility. ___, it allows students to study at their own pace.',
          options: ['That is', 'However', 'Nevertheless'],
          answer: 'That is',
          explanation:
            '"That is" clarifies or explains what flexibility means in this context.',
        },
        {
          text: 'Many argue that social media is harmful. ___, some research suggests it can strengthen relationships.',
          options: ['On the other hand', 'Furthermore', 'As a result'],
          answer: 'On the other hand',
          explanation: '"On the other hand" presents a contrasting viewpoint.',
        },
      ],
    },
    {
      title: 'Fill in the Reference Word',
      type: 'fill-in',
      instructions:
        'Complete each sentence with an appropriate reference word (this, these, such, it, they, etc.).',
      questions: [
        {
          text: 'Pollution affects both human health and the environment. ___ issue requires immediate government action.',
          answer: 'This',
          explanation:
            '"This" refers back to the entire concept of pollution affecting health and environment.',
        },
        {
          text: 'Many students struggle with time management. ___ often leads to increased stress and lower grades.',
          answer: 'This',
          explanation:
            '"This" refers to the problem of struggling with time management.',
        },
        {
          text: 'Electric vehicles and solar panels are becoming more affordable. ___ technologies will play a crucial role in fighting climate change.',
          answer: 'These',
          explanation:
            '"These" refers to the plural technologies mentioned (electric vehicles and solar panels).',
        },
        {
          text: 'Some companies offer unlimited vacation days. ___ policies can improve employee satisfaction.',
          answer: 'Such',
          explanation:
            '"Such" refers to policies of this type (unlimited vacation).',
        },
      ],
    },
    {
      title: 'Identify the Function',
      type: 'select',
      instructions: 'What function does the underlined linking word serve?',
      questions: [
        {
          text: 'The experiment was successful. <u>Nevertheless</u>, further research is needed.',
          options: ['Contrast', 'Addition', 'Cause/Effect'],
          answer: 'Contrast',
          explanation:
            '"Nevertheless" shows contrast—despite success, more research is still needed.',
        },
        {
          text: 'Climate change affects agriculture. <u>Moreover</u>, it threatens coastal cities.',
          options: ['Addition', 'Contrast', 'Example'],
          answer: 'Addition',
          explanation: '"Moreover" adds another effect of climate change.',
        },
        {
          text: 'The population is aging rapidly. <u>Consequently</u>, healthcare costs are rising.',
          options: ['Cause/Effect', 'Contrast', 'Sequence'],
          answer: 'Cause/Effect',
          explanation:
            '"Consequently" shows that rising healthcare costs result from aging population.',
        },
      ],
    },
    {
      title: 'Improve Cohesion',
      type: 'writing',
      instructions:
        'Rewrite these repetitive sentences using reference words, synonyms, and linking devices.',
      questions: [
        {
          text: 'Education is important. Education helps people find jobs. Education improves society.',
          example:
            'Education is fundamentally important as it helps people find employment. Moreover, this leads to broader improvements across society.',
          tip: 'Use "it", synonyms like "employment", and linkers like "moreover".',
        },
        {
          text: 'Cities are growing. Cities face traffic problems. Cities need better public transport.',
          example:
            'As cities continue to expand, they increasingly face traffic congestion. Consequently, such urban areas require significant investment in public transportation.',
          tip: 'Use "they", "such urban areas", synonyms, and cause/effect linkers.',
        },
        {
          text: 'Social media is popular. Social media connects people. Social media can spread misinformation.',
          example:
            'Social media has become increasingly popular because it connects people across the globe. However, these platforms can also spread misinformation rapidly.',
          tip: 'Use "it", "these platforms", and contrast linkers.',
        },
      ],
    },
  ],
  tips: [
    "Don't overuse linking words—natural flow is key",
    'Make sure "this" and "these" have clear references',
    'Use synonyms to avoid repetition',
    'Practice paragraph-level cohesion, not just sentences',
  ],
  dailyPractice: [
    'Identify linking words in IELTS model essays',
    'Rewrite paragraphs to improve cohesion',
    'Practice using reference words correctly',
    'Get feedback on your linking word usage',
  ],
  backPath: '/exams/english/#writing',
  backLabel: 'Back to Writing',
  aiContext:
    'Coherence & Cohesion: Using linking words, reference words, and logical structure to connect ideas smoothly in IELTS Writing.',
};

// Difficulty-variant factory: returns content adapted to the given difficulty level.
// difficulty 1-2: simplified content with basic examples and easier exercises
// difficulty 3: base content (default)
// difficulty 4-5: advanced content with complex examples and harder exercises
export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...coherenceCohesionData,
      title: coherenceCohesionData.title + ' (Foundations)',
      overview: {
        ...coherenceCohesionData.overview,
        duration: '15-20 minutes',
      },
      // Simplified: show only first 2 sections and first exercise set
      sections: coherenceCohesionData.sections.slice(0, Math.max(2, Math.ceil(coherenceCohesionData.sections.length / 2))),
      exercises: coherenceCohesionData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...coherenceCohesionData,
      title: coherenceCohesionData.title + ' (Advanced)',
      overview: {
        ...coherenceCohesionData.overview,
        duration: '30-35 minutes',
      },
      // Advanced: all sections plus advanced tips context
      sections: coherenceCohesionData.sections,
      exercises: coherenceCohesionData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(coherenceCohesionData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS writing tasks.',
      ],
    };
  }
  return coherenceCohesionData;
}
